import { PostgrestSingleResponse, SupabaseClient } from '@supabase/supabase-js';

import {
  CreateMediaManagerTemplate,
  MediaManagerTemplateFilter,
  UpdateMediaManagerTemplate,
} from '../../domain/interfaces/supabase/media-manager/actions';
import { MediaManagerTemplateDto } from '../../domain/interfaces/supabase/media-manager/dto';
import { MediaManagerTemplate } from '../../domain/interfaces/supabase/media-manager/entity';
import mediaManagerTemplateDataMapper from '../../domain/mappers/media-manager-template-data-mapper';

const TEMPLATES_TABLE = 'media_manager_templates';

export default class MediaManager {
  constructor(private supabase: SupabaseClient) {}

  async getTemplates(filter?: MediaManagerTemplateFilter): Promise<MediaManagerTemplate[]> {
    let query = this.supabase
      .from(TEMPLATES_TABLE)
      .select('*')
      .order('created_at', { ascending: true });

    if (filter?.id !== undefined) {
      query = query.eq('id', filter.id);
    }
    if (filter?.tournamentId !== undefined) {
      query = query.eq('tournament_id', filter.tournamentId);
    }
    if (filter?.sportType) {
      query = query.eq('sport_type', filter.sportType);
    }

    const { data, error } = (await query) as PostgrestSingleResponse<MediaManagerTemplateDto[]>;

    if (error) {
      console.error(error);
      throw error;
    }

    return data.map(mediaManagerTemplateDataMapper);
  }

  async getTemplateById(id: number): Promise<MediaManagerTemplate> {
    const { data, error } = (await this.supabase
      .from(TEMPLATES_TABLE)
      .select('*')
      .eq('id', id)
      .single()) as PostgrestSingleResponse<MediaManagerTemplateDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerTemplateDataMapper(data);
  }

  async createTemplate(props: CreateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const { data, error } = (await this.supabase
      .from(TEMPLATES_TABLE)
      .insert({
        name: props.name,
        objects: props.objects,
        version: props.version,
        tournament_id: props.tournamentId,
        sport_type: props.sportType ?? null,
        width: props.width,
        height: props.height,
        order: props.order,
        template_type: props.templateType,
        template_subtypes: [...(props.templateSubtypes ?? [])],
      })
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerTemplateDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerTemplateDataMapper(data);
  }

  async updateTemplate(props: UpdateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const {
      id,
      name,
      objects,
      version,
      sportType,
      width,
      height,
      order,
      templateType,
      templateSubtypes,
      matchLayout,
      playerRanking,
    } = props;

    if (
      name === undefined &&
      objects === undefined &&
      version === undefined &&
      sportType === undefined &&
      width === undefined &&
      height === undefined &&
      order === undefined &&
      templateType === undefined &&
      templateSubtypes === undefined &&
      matchLayout === undefined &&
      playerRanking === undefined
    ) {
      throw new Error('Nothing to update');
    }

    const updates: Partial<MediaManagerTemplateDto> = {};
    if (name !== undefined) updates.name = name;
    if (objects !== undefined) updates.objects = objects;
    if (version !== undefined) updates.version = version;
    if (sportType !== undefined) updates.sport_type = sportType;
    if (width !== undefined) updates.width = width;
    if (height !== undefined) updates.height = height;
    if (order !== undefined) updates.order = order;
    if (templateType !== undefined) updates.template_type = templateType;
    if (templateSubtypes !== undefined) updates.template_subtypes = [...templateSubtypes];
    if (matchLayout !== undefined) updates.match_layout = matchLayout;
    if (playerRanking !== undefined) updates.player_ranking = playerRanking;

    const { data, error } = (await this.supabase
      .from(TEMPLATES_TABLE)
      .update(updates)
      .eq('id', id)
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerTemplateDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerTemplateDataMapper(data);
  }

  async deleteTemplate(id: number): Promise<void> {
    const { error } = await this.supabase.from(TEMPLATES_TABLE).delete().eq('id', id);

    if (error) {
      console.error(error);
      throw error;
    }
  }
}
